<template>
  <DefaultSection class="is-large">
    <Container>
      <h1
        class="is-title is-size-large has-text-weight-bold has-text-centered mb-4"
      >
        {{ title }}
      </h1>
      <div
        v-if="lead"
        class="is-size-normal has-text-weight-medium has-text-centered mb-6"
        v-html="lead"
      />
      <div
        ref="priceList"
        class="columns is-multiline price-list equal-height-columns"
      >
        <div
          v-for="(tier, index) in tiers"
          :key="tier?.id"
          class="column is-one-quarter-desktop is-half is-flex"
        >
          <div v-if="tier" class="price-box">
            <div class="main-info">
              <h3
                class="is-title is-size-4 has-text-weight-bold has-text-centered"
              >
                {{ tier.title }}
              </h3>
              <h4
                class="is-size-normal has-text-weight-semibold mb-6 has-text-centered"
              >
                {{ tier.price }}
              </h4>
              <p class="is-size-normal has-text-weight-medium mb-6">
                {{ tier.lead }}
              </p>

              <VuepalLink
                v-if="tier.link && tier.link.url"
                :to="tier.link.url.path"
                class="button is-large has-text-weight-bold is-size-6"
                :class="getPriceButtonClass(index)"
              >
                {{ tier.link.title }}
              </VuepalLink>
            </div>
            <div class="subinfo">
              <div
                class="has-text-weight-medium is-size-normal content"
                v-html="tier.features"
              />
              <div class="has-text-centered">
                <button
                  v-if="tier.text"
                  class="button is-outlined has-text-weight-bold is-outlined is-size-normal is-small mt-5"
                  :class="getInfoButtonClass(index)"
                  @click="infoModal(tier.text)"
                >
                  Mehr Infos
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </DefaultSection>
</template>

<script setup lang="ts">
import { useOruga } from '@oruga-ui/oruga-next'
import ParagraphPricesPriceDetails from '~/components/Paragraph/Prices/PriceDetails/index.vue'
import type { ParagraphPricesFragment } from '#graphql-operations'

defineProps<ParagraphPricesFragment>()

defineBlokkli({
  bundle: 'prices',
  propsFieldMapping: {
    title: null,
    lead: null,
    tiers: { type: 'field', name: 'field_tiers' },
  },
})

const oruga = useOruga()

function infoModal(content: string) {
  oruga.modal.open({
    component: ParagraphPricesPriceDetails,
    props: { content },
    trapFocus: true,
  })
}

const priceList = ref<HTMLElement | null>(null)

onMounted(() => {
  setEqualHeight()
  window.addEventListener('resize', setEqualHeight)
})

function setEqualHeight() {
  const elements = [
    ...(priceList.value?.querySelectorAll<HTMLElement>('.main-info') ?? []),
  ]
  let maxHeight = 0

  // Reset all heights
  for (const element of elements) {
    element.style.height = 'auto'
  }

  // Find the maximum height
  for (const element of elements) {
    if (element.offsetHeight > maxHeight) {
      maxHeight = element.offsetHeight
    }
  }

  // Set the max height to all elements
  for (const element of elements) {
    element.style.height = `${maxHeight}px`
  }
}

function getPriceButtonClass(index: number) {
  let buttonClass = 'is-lessmess-desk'
  if (index === 0) {
    buttonClass = 'is-sky-blue'
  }
  return buttonClass
}

function getInfoButtonClass(index: number) {
  let buttonClass = 'is-sky-blue'
  if (index > 1) {
    buttonClass = 'is-white'
  }
  return buttonClass
}

// You can add any other logic or functions here
</script>

<style scoped lang="scss">
.price-list {
  .column:nth-child(2) {
    .price-box {
      background-color: $pale-gray-blue;
    }
  }
  .column:nth-child(3) {
    .price-box {
      background-color: $bright-gray-blue;
      color: $white;
    }
  }
  .column:nth-child(4) {
    .price-box {
      background-color: $gray-blue;
      color: $white;
    }
  }
}

.price-box {
  border-radius: 10px;
  background-color: $background-gray;
  padding: 2rem 2rem;
  box-shadow: 0 12px 32px -8px rgba(26, 26, 26, 0.24);
  .button {
    margin-top: auto;
  }

  .main-info {
    display: flex;
    flex-flow: column nowrap;
  }

  .subinfo {
    padding-top: 2rem;
    .content {
      ::v-deep(ol) {
        list-style-type: none;
        > li:before {
          content: '-';
          position: absolute;
          margin-left: -1rem;
        }
      }
    }
  }
}

.main-info {
  display: flex;
  flex-flow: column nowrap;
}
</style>
